import React, { useState, useEffect } from 'react';
import { get, post, put } from '../services/apiController';
import { TABLE_DEFAULT_LIMIT, DEBOUNCE_DELAY } from '../util/constant';
import { Table, Form, Modal, Row, Col, FloatingLabel } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useDebounce from './useDebounce';
import { FiSave } from 'react-icons/fi';
import { months } from '../util/helper';
import Label from '../components/atoms/Label';
import { ViewIcon } from './icons';
import Breadcrumb from '../components/Breadcrumb';

const BillDetail = () => {
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [billParametersData, setBillParametersData] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [locationList, setLocationList] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [billParameterForm, setBillParameterForm] = useState('');
  const [showBillParameterModal, setShowBillParameterModal] = useState(false);

  const d = new Date();

  const [state, setState] = useState({
    billType: '',
    companyId: '',
    locationType: '',
    locationId: '',
    month: d.getMonth() + 1,
    year: d.getFullYear(),
  });

  function getBillDetails() {
    try {
      get(
        `/bill-details/?page=${currentPage}&limit=${TABLE_DEFAULT_LIMIT}&billType=${state.billType}&companyId=${state.companyId}&locationType=${state.locationType}&locationId=${state.locationId}&month=${state.month}&year=${state.year}`
      ).then((response) => {
        const rows = response.data;
        setBillParametersData(response.data);
        setTotalRecords(response.data.length);
        setTotalAmount(rows.reduce((accumulator, row) => accumulator + row.amount, 0));
      });
    } catch (err) {
      toast.error(err.response.data.data.message);
    }
  }

  function getCompanies() {
    try {
      get(`/companies`).then((response) => {
        setCompanyList(response.data.rows);
      });
    } catch (err) {
      toast.error(err?.response?.data?.data?.message || 'something went wrong');
    }
  }

  const getLocations = () => {
    try {
      get(`/branches`).then((response) => {
        setLocationList(response.data.rows);
      });
    } catch (err) {
      toast.error(err.response.data.data.message);
    }
  };

  const loadLocationData = (locationId) => {
    return locationList.find((item) => {
      return item?.id === parseInt(locationId);
    });
  };

  const handleView = (e, id) => {
    e.preventDefault();
    try {
      get('/bill-parameters/?id=' + id).then((res) => {
        if (res.data.rows.length) {
          const Data = res.data.rows[0];
          let locationData = loadLocationData(Data?.locationId);
          setBillParameterForm({ ...Data, locationType: locationData?.type, companyId: locationData?.companyId });
          setShowBillParameterModal(true);
        } else {
          toast.error('No bill parameter found.');
        }
      });
    } catch (error) {
      console.log('error', error);
      toast.error(error?.response?.data?.data?.rows?.message || 'Something went wrong');
    }
  };

  const handleChange = (e, index) => {
    e.preventDefault();
    const { name, value } = e.target;
    const updatedData = [...billParametersData];
    updatedData[index] = { ...updatedData[index], [name]: value };
    setBillParametersData(updatedData);
  };

  const handleSubmit = (e, billParameterId, index) => {
    try {
      if (billParametersData[index].billDetailId) {
        put(
          { ...billParametersData[index], month: state.month, year: state.year },
          '/bill-details/' + billParametersData[index].billDetailId
        )
          .then((res) => {
            toast.success(res.data.message);
            getBillDetails();
          })
          .catch((err) => toast.error(err?.response?.data?.data?.message) || 'Something went wrong');
      } else {
        post({ ...billParametersData[index], month: state.month, year: state.year, billParameterId }, '/bill-details')
          .then((res) => {
            toast.success(res.data.message);
            getBillDetails();
          })
          .catch((err) => toast.error(err?.response?.data?.data?.message) || 'Something went wrong');
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  useDebounce(
    () => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        getBillDetails();
      }
    },
    [state],
    DEBOUNCE_DELAY
  );

  useEffect(() => {
    getCompanies();
    getLocations();
  }, []);

  useEffect(() => {
    getBillDetails();
  }, [currentPage]);

  return (
    <React.Fragment>
      <ToastContainer />
      <Breadcrumb
        navigation={[
          { link: '#', title: 'Store Operations' },
          { link: '#', title: 'Bill Management' },
          { link: '#', title: 'Bill Details' },
        ]}
      />
      <div className="card custom_card">
        <div className="d-flex justify-content-between card-header white-color" style={{ padding: '1rem 1.7rem' }}>
          <div className="col-12 col-lg-2">
            <h4 className="card-title mb-0" style={{ fontSize: '16px' }}>
              Bills | {totalRecords ?? 0}
            </h4>
          </div>
          <div className="col-12 col-lg-2" style={{ marginRight: '12px' }}>
            <Form.Select
              value={state.billType}
              onChange={(e) => setState({ ...state, billType: e.target.value })}
              placeholder="Bill Type"
              className="input"
            >
              <option value="">Select Bill Type</option>
              <option value="Electricity">Electricity</option>
              <option value="Water">Water</option>
              <option value="Tele/Wi-Fi">Tele/Wi-Fi</option>
            </Form.Select>
          </div>
          <div className="col-12 col-lg-8 text-end">
            <div className="row align-items-center">
              <div className="col-4">
                <Form.Select
                  value={state.companyId}
                  onChange={(e) => {
                    setState({
                      ...state,
                      companyId: e.target.value,
                      locationType: '',
                      locationId: '',
                    });
                  }}
                  className="input"
                >
                  <option value="">Select Company</option>
                  {companyList.map((company) => {
                    return (
                      <option key={company?.id} value={company?.id}>
                        {company?.name}
                      </option>
                    );
                  })}
                </Form.Select>
              </div>
              <div className="col-4">
                <Form.Select
                  value={state.locationType}
                  className="input mb-2"
                  onChange={(e) => {
                    setState({ ...state, locationType: e.target.value, locationId: '' });
                  }}
                >
                  <option value="">Select Location Type</option>
                  <option value="office">Office</option>
                  <option value="factory">Factory</option>
                  <option value="outlet">Outlet</option>
                  <option value="staff-room">Staff Room</option>
                </Form.Select>
                <Form.Select className="input" value={state.month} onChange={(e) => setState({ ...state, month: e.target.value })}>
                  <option value="">Select Month</option>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((item) => {
                    return (
                      <option key={item} value={item}>
                        {months[item]}
                      </option>
                    );
                  })}
                </Form.Select>
              </div>
              <div className="col-4">
                <Form.Select
                  value={state.locationId}
                  onChange={(e) => setState({ ...state, locationId: e.target.value })}
                  style={{ width: '170px' }}
                  className="input mb-2"
                >
                  <option value="">Select Location</option>
                  {locationList
                    .filter((item) => {
                      return item?.type === state.locationType && item?.companyId === parseInt(state?.companyId);
                    })
                    .map((item) => {
                      return (
                        <option key={item?.id} value={item?.id}>
                          {item?.name}
                        </option>
                      );
                    })}
                </Form.Select>
                <Form.Select
                  className="input"
                  value={state.year}
                  onChange={(e) => setState({ ...state, year: e.target.value })}
                  style={{ width: '170px' }}
                >
                  <option value="">Select Year</option>
                  {[2020, 2021, 2022, 2023, 2024, 2025].map((item) => {
                    return (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    );
                  })}
                </Form.Select>
              </div>
            </div>
          </div>
        </div>
        <div className="card-body px-0 vehicle-requests-table pt-0 pb-0">
          <div className="row">
            <div className="col">
              <Table responsive striped bordered className="bill-detail-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Company</th>
                    <th>Location Type</th>
                    <th>Location</th>
                    <th>Location Ref Name</th>
                    <th>Bill Type</th>
                    <th>Due Date</th>
                    <th>Amount</th>
                    <th>Payment Status</th>
                    <th>Comments</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {billParametersData.map((item, index) => {
                    return (
                      <tr key={item?.id} className={item.isPaid !== 1 ? 'unpaid' : ''}>
                        <td>{(currentPage - 1) * TABLE_DEFAULT_LIMIT + (index + 1)}</td>
                        <td>{item?.company_name}</td>
                        <td style={{ textTransform: 'capitalize' }}>{item?.branch_type}</td>
                        <td>{item?.branch_name}</td>
                        <td>{item?.locationRefName}</td>
                        <td>{item?.billType}</td>
                        <td>{`${item?.dueDate} th`}</td>
                        <td>
                          <input
                            value={item.amount ?? ''}
                            name="amount"
                            onChange={(e) => handleChange(e, index)}
                            type="number"
                            placeholder="Enter amount"
                          />
                        </td>
                        <td className="actions">
                          <select name="isPaid" value={item?.isPaid === null ? '' : item?.isPaid} onChange={(e) => handleChange(e, index)}>
                            <option value="">Select</option>
                            <option value="1">Paid</option>
                            <option value="0">Not Paid</option>
                          </select>
                        </td>
                        <td>
                          <input
                            value={item.comment}
                            name="comment"
                            onChange={(e) => handleChange(e, index)}
                            type="text"
                            placeholder="Comments"
                          />
                        </td>
                        <td>
                          <FiSave
                            title="Save bill details"
                            style={{ cursor: 'pointer', height: '25px', width: '25px', color: 'green', marginRight: '6px' }}
                            onClick={(e) => handleSubmit(e, item?.id, index)}
                          />
                          <ViewIcon
                            onClick={(e) => handleView(e, item?.id)}
                            style={{ cursor: 'pointer', width: '16px', color: 'purple ' }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td colSpan={9} className="text-end">
                      <Label className="bold-text-700">Total Amount</Label>
                    </td>
                    <td colSpan={4} className="text-start">
                      <Label>{totalAmount}</Label>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>
        </div>
      </div>
      <Modal size="lg" show={showBillParameterModal} backdrop="static" keyboard={false} className="request-modal">
        <Modal.Header>
          <Modal.Title>Bill Parameter Details</Modal.Title>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => {
              setShowBillParameterModal(false);
              setBillParameterForm('');
            }}
          ></button>
        </Modal.Header>
        <Modal.Body>
          <Form autoComplete="off">
            <Row>
              <Col xs={4}>
                <FloatingLabel controlId="floatingInput" label="Company" className="mb-3">
                  <Form.Select className="input" disabled value={billParameterForm.companyId} name="companyId" placeholder="Company Id">
                    <option>Select Company</option>
                    {companyList.map((item) => (
                      <option key={item.id} id={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </Form.Select>
                </FloatingLabel>
              </Col>
              <Col xs={4}>
                <FloatingLabel label="Location Type" className="mb-3">
                  <Form.Select
                    className="input"
                    disabled
                    value={billParameterForm.locationType}
                    name="locationType"
                    placeholder="Location Type"
                  >
                    <option value="">Select Location Type</option>
                    <option value="outlet">Outlet</option>
                    <option value="office">Office</option>
                    <option value="factory">factory</option>
                    <option value="staff-room">Staff Room</option>
                  </Form.Select>
                </FloatingLabel>
              </Col>
              <Col xs={4}>
                <FloatingLabel controlId="floatingInput" label="Location" className="mb-3">
                  <Form.Select disabled value={billParameterForm.locationId} name="locationId" className="input mb-2">
                    <option value="">Select Location</option>
                    {locationList
                      .filter((item) => {
                        return item?.type === billParameterForm.locationType && item?.companyId === parseInt(billParameterForm.companyId);
                      })
                      .map((item) => {
                        return (
                          <option key={item?.id} value={item?.id}>
                            {item?.name}
                          </option>
                        );
                      })}
                  </Form.Select>
                </FloatingLabel>
              </Col>
              <Col xs={4}>
                <FloatingLabel controlId="floatingInput" label="Location Ref. Name" className="mb-3">
                  <Form.Control
                    className="input"
                    disabled
                    name="locationRefName"
                    value={billParameterForm.locationRefName}
                    type="text"
                    placeholder="Location Ref. Name"
                  />
                </FloatingLabel>
              </Col>
              <Col xs={4}>
                <FloatingLabel controlId="floatingInput" label="Bill Type" className="mb-3">
                  <Form.Select
                    className="input"
                    disabled
                    name="billType"
                    value={billParameterForm.billType}
                    type="text"
                    placeholder="Bill Type"
                  >
                    <option value="">Select Bill Type</option>
                    <option value="Electricity">Electricity</option>
                    <option value="Water">Water</option>
                    <option value="Tele/Wi-Fi">Tele/Wi-Fi</option>
                  </Form.Select>
                </FloatingLabel>
              </Col>
              <Col xs={4}>
                <FloatingLabel controlId="floatingInput" label="Account ID" className="mb-3">
                  <Form.Control
                    className="input"
                    disabled
                    name="accountId"
                    value={billParameterForm.accountId}
                    type="text"
                    placeholder="Account ID"
                  />
                </FloatingLabel>
              </Col>
              <Col xs={4}>
                <FloatingLabel controlId="floatingInput" label="Location Owner Name" className="mb-3">
                  <Form.Control
                    className="input"
                    disabled
                    name="locationOwnerName"
                    value={billParameterForm.locationOwnerName}
                    type="text"
                    placeholder="Location Owner Name"
                  />
                </FloatingLabel>
              </Col>
              <Col xs={4}>
                <FloatingLabel controlId="floatingInput" label="Due Date" className="mb-3">
                  <Form.Select disabled value={billParameterForm.dueDate} name="dueDate" className="input mb-2">
                    <option value="">Select due date</option>
                    {Array.from({ length: 31 }, (_, index) => index + 1).map((item) => {
                      return (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      );
                    })}
                  </Form.Select>
                </FloatingLabel>
              </Col>
              <Col xs={4}>
                <FloatingLabel controlId="floatingInput" label="Remarks" className="mb-3">
                  <Form.Control
                    disabled
                    className="input"
                    name="remarks"
                    value={billParameterForm.remarks}
                    type="text"
                    placeholder="Remarks"
                  />
                </FloatingLabel>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default BillDetail;
